import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  useMediaQuery,
  useTheme,
  Stack,
  Snackbar,
  InputAdornment,
  Skeleton,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import axiosInstance from '../../api/axiosConfig';

const Students = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [branches, setBranches] = useState([]);

  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('');

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalStudents, setTotalStudents] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);

  const [toast, setToast] = useState({ open: false, message: '' });
const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    registrationId: '',
    fullName: '',
    fatherName: '',
    dob: '',
    gender: '',
    mobile: '',
    email: '',
    address: '',
    photoUrl: '',
    courseId: '',
    batchYear: '',
    branchId: '',
    admissionDate: '',
  });

  // ✅ Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, courseFilter]);

  // ✅ Initial loads
  useEffect(() => {
  fetchCourses();
  fetchBranches();
}, []);

  useEffect(() => {
    fetchStudents();
  }, [page, debouncedSearch, courseFilter]);

  const fetchCourses = async () => {
    try {
      const res = await axiosInstance.get('/courses');
      const data = res.data.data || res.data;
      setCourses(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load courses');
    }
  };

  const fetchBranches = async () => {
  try {
    const res = await axiosInstance.get('/branch/active');

    const data = res.data.data || res.data;

    setBranches(
      Array.isArray(data)
        ? data
        : []
    );
  } catch {
    showToast(
      'Failed to load branches'
    );
  }
};

  const fetchStudents = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get('/students/search', {
        params: {
          search: debouncedSearch || undefined,
          courseId: courseFilter || undefined,
          pageNumber: page,
          pageSize,
        },
      });

      setStudents(res.data.data || []);
      setTotalStudents(res.data.totalRecords || 0);
      setError(null);
    } catch {
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message) => {
    setToast({ open: true, message });
  };

  const handleOpenDialog = async (student = null) => {
    if (student) {
      try {
        setDialogLoading(true);

        const res = await axiosInstance.get(`/students/${student.studentId}`);
        const s = res.data;

        setEditingStudent(s);

        setFormData({
          registrationId: s.registrationId || '',
          fullName: s.fullName || '',
          fatherName: s.fatherName || '',
          dob: s.dob?.split('T')[0] || '',
          gender: s.gender || '',
          mobile: s.mobile || '',
          email: s.email || '',
          address: s.address || '',
          photoUrl: s.photoUrl || '',
          courseId: s.courseId || '',
          batchYear: s.batchYear || '',
          branchId: s.branchId || '',
          admissionDate: s.admissionDate?.split('T')[0] || '',
        });

        setOpenDialog(true);
      } catch {
        showToast('Failed to load student details');
      } finally {
        setDialogLoading(false);
      }
    } else {
      setEditingStudent(null);
      setFormData({
        registrationId: '',
        fullName: '',
        fatherName: '',
        dob: '',
        gender: '',
        mobile: '',
        email: '',
        address: '',
        photoUrl: '',
        courseId: '',
        batchYear: '',
        branchId: '',
        admissionDate: '',
      });
      setOpenDialog(true);
    }
  };

  const handleSaveStudent = async () => {
    try {
      if (editingStudent) {
        await axiosInstance.put(`/students/${editingStudent.studentId}`, formData);
        showToast('Student updated successfully');
      } else {
        await axiosInstance.post('/students', formData);
        showToast('Student added successfully');
      }

      setOpenDialog(false);
      fetchStudents();
    } catch {
      showToast('Save failed');
    }
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Delete this student?')) return;

    try {
      await axiosInstance.delete(`/students/${id}`);
      showToast('Student deleted');
      fetchStudents();
    } catch {
      showToast('Delete failed');
    }
  };

  const totalPages = Math.ceil(totalStudents / pageSize);

  return (
    <Box>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Student Management
        </Typography>

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Student
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search student"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Course</InputLabel>
                <Select
                  value={courseFilter}
                  label="Course"
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  <MenuItem value="">All Courses</MenuItem>
                  {courses.map(course => (
                    <MenuItem key={course.courseId} value={course.courseId}>
                      {course.courseName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Reg No</TableCell>
              <TableCell sx={{ color: '#fff' }}>Name</TableCell>
              {!isMobile && <TableCell sx={{ color: '#fff' }}>Course</TableCell>}
              {!isMobile && <TableCell sx={{ color: '#fff' }}>Mobile</TableCell>}
              <TableCell sx={{ color: '#fff' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))
            ) : students.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No students found
                </TableCell>
              </TableRow>
            ) : (
              students.map(student => (
                <TableRow key={student.studentId} hover>
                  <TableCell>{student.registrationNumber}</TableCell>
                  <TableCell>{student.fullName}</TableCell>
                  {!isMobile && <TableCell>{student.courseName}</TableCell>}
                  {!isMobile && <TableCell>{student.mobile}</TableCell>}
                  <TableCell align="center">
                    <IconButton onClick={() => handleOpenDialog(student)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeleteStudent(student.studentId)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen={isMobile} maxWidth="md" fullWidth>
        <DialogTitle>{editingStudent ? 'Edit Student' : 'Add Student'}</DialogTitle>

        <DialogContent dividers>
          {dialogLoading ? <CircularProgress /> : (
            <Grid container spacing={2} mt={1}>

              {/* <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Registration ID" value={formData.registrationId} disabled />
              </Grid> */}

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" name="fullName" value={formData.fullName} onChange={(e)=>setFormData({...formData,fullName:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Father Name" name="fatherName" value={formData.fatherName} onChange={(e)=>setFormData({...formData,fatherName:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="date" label="DOB" InputLabelProps={{ shrink: true }} name="dob" value={formData.dob} onChange={(e)=>setFormData({...formData,dob:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Gender" name="gender" value={formData.gender} onChange={(e)=>setFormData({...formData,gender:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Mobile" name="mobile" value={formData.mobile} onChange={(e)=>setFormData({...formData,mobile:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" name="email" value={formData.email} onChange={(e)=>setFormData({...formData,email:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Batch Year" name="batchYear" value={formData.batchYear} onChange={(e)=>setFormData({...formData,batchYear:e.target.value})} />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label="Address" multiline rows={2} name="address" value={formData.address} onChange={(e)=>setFormData({...formData,address:e.target.value})} />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label="Photo URL" name="photoUrl" value={formData.photoUrl} onChange={(e)=>setFormData({...formData,photoUrl:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="date" label="Admission Date" InputLabelProps={{ shrink: true }} name="admissionDate" value={formData.admissionDate} onChange={(e)=>setFormData({...formData,admissionDate:e.target.value})} />
              </Grid>

              <Grid item xs={12} sm={6}>
  <FormControl fullWidth>
    <InputLabel>Branch</InputLabel>

    <Select
      value={formData.branchId}
      label="Branch"
      onChange={(e) =>
        setFormData({
          ...formData,
          branchId: e.target.value
        })
      }
    >
      {branches.map(branch => (
        <MenuItem
          key={branch.branchId}
          value={branch.branchId}
        >
          {branch.branchName}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Course</InputLabel>
                  <Select name="courseId" value={formData.courseId} onChange={(e)=>setFormData({...formData,courseId:e.target.value})} label="Course">
                    {courses.map(course => (
                      <MenuItem key={course.courseId} value={course.courseId}>
                        {course.courseName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStudent}>
            {editingStudent ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast({ open: false, message: '' })}
        message={toast.message}
      />

    </Box>
  );
};

export default Students;